// CustomSelect.jsx
import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Button,
  Box,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

const CustomSelect = ({
  children,
  value,
  onChange,
  placeholder = 'Select…',
  maxWidth = '100%',
  variant = 'default',
  ...buttonProps
}) => {
  const isPlaceholder = !value;
  const selectedLabel = React.Children.toArray(children)
    .find(c => c.props.value === value)
    ?.props.children;

  // Style variants
  const getVariantStyles = () => {
    if (variant === 'light') {
      return {
        button: {
          bg: 'primary.100',
          color: isPlaceholder ? 'primary.900' : 'secondary.100',
          borderColor: 'primary.900',
          boxShadow: 'none',
          _hover: { 
            borderColor: 'secondary.500', 
            color: 'secondary.500' 
          },
          _focus: {
            borderColor: 'secondary.100',
            color: 'secondary.100',
            boxShadow: 'none',
          },
          _expanded: { 
            borderColor: 'secondary.100' 
          }
        },
        menuList: {
          bg: 'primary.100'
        },
        menuItem: {
          bg: 'primary.100',
          color: 'secondary.100',
          _hover: { 
            bg: 'primary.500', 
            color: 'secondary.100' 
          },
          _checked: {
            bg: 'primary.500',
            color: 'secondary.100',
            fontWeight: 'bold',
          }
        }
      };
    }
    
    // Default variant
    return {
      button: {
        bg: 'secondary.900',
        color: isPlaceholder ? 'secondary.100' : 'primary.500',
        borderColor: 'secondary.900',
        _hover: { 
          bg: 'secondary.700', 
          color: 'secondary.900' 
        },
        _focus: {
          borderColor: 'brand.500',
          color: 'secondary.100',
        },
        _expanded: { 
          bg: 'secondary.700' 
        }
      },
      menuList: {
        bg: 'secondary.900'
      },
      menuItem: {
        bg: 'secondary.900',
        color: 'primary.500',
        _hover: { 
          bg: 'secondary.700', 
          color: 'primary.100' 
        },
        _checked: {
          bg: 'secondary.700',
          color: 'primary.100',
          fontWeight: 'bold',
        }
      }
    };
  };

  const styles = getVariantStyles();

  // ref + state for button width
  const btnRef = useRef(null);
  const [btnWidth, setBtnWidth] = useState('auto');
  const updateWidth = () => {
    if (btnRef.current) {
      const w = btnRef.current.getBoundingClientRect().width;
      setBtnWidth(`${w}px`);
    }
  };

  // measure when mount, value/children change, or window resizes
  useLayoutEffect(updateWidth, [value, children]);
  useEffect(() => {
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  return (
    <Menu closeOnSelect>
      {/* Trigger */}
      <MenuButton
        ref={btnRef}
        as={Button}
        rightIcon={<ChevronDownIcon />}
        textAlign="left"
        w="100%"
        maxW={maxWidth}
        px={4}
        py={2}
        border="1px solid"
        borderRadius="md"
        {...styles.button}
        {...buttonProps}
      >
        <Box
          flex="1"
          textAlign="left"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {selectedLabel || placeholder}
        </Box>
       
      </MenuButton>

      {/* Dropdown */}
      <MenuList
        p={0}
        style={{ width: btnWidth, maxWidth }}
        {...styles.menuList}
      >
        <MenuOptionGroup type="radio" value={value} onChange={onChange}>
          {React.Children.map(children, child => (
            <MenuItemOption
              key={child.props.value}
              value={child.props.value}
              {...styles.menuItem}
            >
              {child.props.children}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
};

export default CustomSelect;